<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-page-container custom="1180px">
        <app-flex-grid
          :rows="[
            ['220px', '220px', '220px', '220px', '220px'],
            ['220px', '220px', '220px'],
            ['220px', '220px', '220px'],
            [1],
            ['220px', '220px', '220px', '220px'],
            ['220px', '220px', '220px']
          ]"
          :gap-rows="30"
        >
          <template #header-1>
            <app-cells gap="10px" :indent="false" :margin-bottom="20">
              <img alt="personal" src="@/assets/img/personal-details.svg">
              <h3 class="title">Personal details</h3>
            </app-cells>
          </template>
          <template #col-1-1>
            <app-form-group label="First name" label-for="first_name" class="form-group--no-indent" required>
              <app-input
                @change.native="$v.personal.first_name.$touch()"
                v-model="personal.first_name"
                id="first_name"
                name="first_name"
                placeholder="John"
                :error="$v.personal.first_name.$error"
              />
              <template #error>
                <div v-if="$v.personal.first_name.$dirty && !$v.personal.first_name.required">Required field</div>
              </template>
            </app-form-group>
          </template>
          <template #col-1-2>
            <app-form-group label="Middle name" label-for="middle_name" class="form-group--no-indent">
              <app-input
                v-model="personal.middle_name"
                id="middle_name"
                name="middle_name"
                placeholder="Emily"
              />
            </app-form-group>
          </template>
          <template #col-1-3>
            <app-form-group label="Last name" label-for="last_name" class="form-group--no-indent" required>
              <app-input
                @change.native="$v.personal.last_name.$touch()"
                v-model="personal.last_name"
                id="last_name"
                name="last_name"
                placeholder="Doe"
                :error="$v.personal.first_name.$error"
              />
              <template #error>
                <div v-if="$v.personal.last_name.$dirty && !$v.personal.last_name.required">Required field</div>
              </template>
            </app-form-group>
          </template>
          <template #col-1-4>
            <app-form-group label="Date of birth" class="form-group--no-indent">
              <app-date-picker
                @input="onCheckBirth"
                v-model="personal.dob"
                placeholder="Specify a date"
                :error="$v.personal.dob.$error || $v.age.$invalid"
              />
              <template #error>
                <div v-if="$v.personal.dob.$dirty && !$v.age.between">Age must be from 16 to 90 years
                  old{{ age ? `, current age: ${age}` : '' }}
                </div>
              </template>
            </app-form-group>
          </template>
          <template #col-1-5>
            <app-form-group class="form-group--center form-group--no-indent" label="Gender">
              <app-cells :indent="false">
                <app-radio
                  v-for="item in genders_options"
                  :key="item.code"
                  v-model.number="personal.gender"
                  :checked="personal.gender.code"
                  :value="item.code"
                  name="gender"
                >
                  <template #label>{{ item.name }}</template>
                </app-radio>
              </app-cells>
            </app-form-group>
          </template>
          <template #col-2-1>
            <app-form-group label="Race" class="form-group--no-indent">
              <app-select
                v-model="personal.race"
                :options="race_options"
                :clearable="false"
                placeholder="Race"
              />
            </app-form-group>
          </template>
          <template #col-2-2>
            <app-form-group label="Marital status" class="form-group--no-indent">
              <app-select
                v-model="personal.marital_status"
                :options="marital_status_options"
                :clearable="false"
                placeholder="Marital status"
              />
            </app-form-group>
          </template>
          <template #col-2-3>
            <app-form-group label="Disability" class="form-group--no-indent">
              <app-select
                v-model="personal.disability"
                :options="disability_options"
                :clearable="false"
                placeholder="Disability"
              />
            </app-form-group>
          </template>
          <template #col-3-1>
            <app-form-group label="Region" class="form-group--no-indent">
              <app-select
                v-model="personal.region"
                :options="regions_options"
                placeholder="Region"
              />
            </app-form-group>
          </template>
          <template #col-3-2>
            <app-form-group label="Village" class="form-group--no-indent">
              <app-select
                v-model="personal.village"
                :options="villages_options"
                placeholder="Village"
              />
            </app-form-group>
          </template>
          <template #col-3-3>
            <app-form-group label="Resident" class="form-group--no-indent">
              <app-switcher v-model="personal.resident"/>
            </app-form-group>
          </template>
          <template #col-4-1>
            <app-form-group class="form-group--no-indent">
              <app-cropper-web-cam
                ref="cropper"
                v-model="personal.crop"
                :aspectRatio="3/4"
              >
                <template #title>Upload employee photo</template>
                <template #text>
                  <ul>
                    <li>Accepted formats: JPG, JPEG, BMP, PNG, TIFF, TIF</li>
                    <li>Maximum size: 5 MB</li>
                  </ul>
                </template>
              </app-cropper-web-cam>
            </app-form-group>
          </template>
          <template #header-5>
            <app-cells gap="10px" :indent="false" :margin-top="10" :margin-bottom="20">
              <img alt="personal" src="@/assets/img/contact-details.svg">
              <h3 class="title">Contacts details</h3>
            </app-cells>
          </template>
          <template #col-5-1>
            <app-form-group label="Main phone" class="form-group--no-indent">
              <app-phone
                @change.native="$v.personal.phone.$touch()"
                v-model="personal.phone"
                default_сountry="TZ"
                placeholder="+255 786 350 350"
                :error="$v.personal.phone.$dirty && personal.phone === 0"
              />
              <template #error>
                <div v-if="$v.personal.phone.$dirty && personal.phone === 0">
                  Invalid phone number format
                </div>
              </template>
            </app-form-group>
          </template>
          <template #col-5-2>
            <app-form-group class="form-group--center form-group--no-indent" label="Messengers">
              <app-cells :indent="false">
                <app-checkbox v-model="personal.phone_telegram" label="Telegram"/>
                <app-checkbox v-model="personal.phone_whatsapp" label="Whatsapp"/>
              </app-cells>
            </app-form-group>
          </template>
          <template #col-5-3>
            <app-form-group label="Telegram ID" label-for="telegram_id" class="form-group--no-indent">
              <app-input
                v-model="personal.telegram_id"
                id="telegram_id"
                name="telegram_id"
                placeholder="Telegram ID"
              />
            </app-form-group>
          </template>
          <template #col-5-4>
            <app-form-group label="Telegram username" label-for="telegram_username" class="form-group--no-indent">
              <app-input
                v-model="personal.telegram_username"
                id="telegram_username"
                name="telegram_username"
                placeholder="Telegram username"
              />
            </app-form-group>
          </template>
          <template #col-6-1>
            <app-form-group label="Additional phone" class="form-group--no-indent">
              <app-phone
                @change.native="$v.personal.additional_phone.$touch()"
                v-model="personal.additional_phone"
                default_сountry="TZ"
                placeholder="+255 786 350 350"
                :error="$v.personal.additional_phone.$dirty && personal.additional_phone === 0"
              />
              <template #error>
                <div v-if="$v.personal.additional_phone.$dirty && personal.additional_phone === 0">
                  Invalid phone number format
                </div>
              </template>
            </app-form-group>
          </template>
          <template #col-6-2>
            <app-form-group class="form-group--center form-group--no-indent" label="Messengers">
              <app-cells :indent="false">
                <app-checkbox v-model="personal.additional_phone_telegram_available" label="Telegram"/>
                <app-checkbox v-model="personal.additional_phone_whatsapp_available" label="Whatsapp"/>
              </app-cells>
            </app-form-group>
          </template>
          <template #col-6-3>
            <app-form-group label="Email" label-for="email" class="form-group--no-indent">
              <app-input
                v-model="personal.email"
                id="email"
                name="email"
                placeholder="mail@example.com"
                type="email"
                :readonly="is_email_readonly"
                :title="is_email_readonly ? 'Only a General Manager can change email for an active User account.' : null"
              />
            </app-form-group>
          </template>
        </app-flex-grid>
        <app-cells :indent="false" position="end">
          <app-button ref="submit_personal" @click.native="onCheckPersonalForm">
            Save
          </app-button>
        </app-cells>
      </app-page-container>
      <personal-cabinet-access
        ref="personalCabinetAccess"
        :employee-id="$route.params.id"
        @state-change="onPersonalCabinetStateChange"
      />
      <app-page-container v-if="is_safari_tour_guide" class="safari-guide-section" custom="1180px">
        <label class="safari-guide-expert" :class="{ 'safari-guide-expert--active': strong_in_birds }">
          <input v-model="strong_in_birds" type="checkbox" @change="onSaveBirdwatchingExpert">
          <span class="safari-guide-expert__check">✓</span>
          <span class="safari-guide-expert__content">
            <strong>🦜 Birdwatching expert</strong>
            <span>Guide is strong in birdwatching — shown on the Safari schedule</span>
          </span>
        </label>
        <label class="safari-guide-expert" :class="{ 'safari-guide-expert--active': great_with_kids }">
          <input v-model="great_with_kids" type="checkbox" @change="onSaveGuideSkill('great_with_kids', 'Great with kids')">
          <span class="safari-guide-expert__check">✓</span>
          <span class="safari-guide-expert__content">
            <strong>🧸 Great with kids</strong>
            <span>Guide works especially well with children — shown on the Safari schedule</span>
          </span>
        </label>
        <label class="safari-guide-expert" :class="{ 'safari-guide-expert--active': influencer_experienced }">
          <input v-model="influencer_experienced" type="checkbox" @change="onSaveGuideSkill('influencer_experienced', 'Influencer experience')">
          <span class="safari-guide-expert__check">✓</span>
          <span class="safari-guide-expert__content">
            <strong>📸 Influencer-experienced</strong>
            <span>Guide has experience working with influencers — shown on the Safari schedule</span>
          </span>
        </label>
        <div class="spoken-languages">
          <h3 class="spoken-languages__title">Spoken languages</h3>
          <div v-for="(item, index) in spoken_languages" :key="item.id || `new-${index}`" class="spoken-languages__row">
            <div class="spoken-languages__language">
              <app-select v-model="item.language" :options="language_options" :reduce="language => language.code" label="name" placeholder="Select language..." :readonly="Boolean(item.id)"/>
            </div>
            <div class="spoken-languages__level">
              <app-select v-model="item.level" :options="language_level_options" :reduce="level => level.code" label="name" placeholder="Level" :readonly="!item.language"/>
            </div>
          <app-button size="small" :disabled="!item.language || !item.level || saving_spoken_language === index" @click.native="onSaveSpokenLanguage(item, index)">Save</app-button>
          <app-button size="small" theme="dark" @click.native="onDeleteSpokenLanguage(item, index)">Delete</app-button>
          </div>
          <button class="spoken-languages__add" type="button" @click="onAddSpokenLanguage"><span>+</span>Add language</button>
          <div class="spoken-languages__hint">Level becomes available after a language is selected</div>
        </div>
      </app-page-container>
      <app-cells>
        <h2 class="title">Family</h2>
        <app-button size="small" @click.native="onOpenModalForm('family')">Add family member</app-button>
      </app-cells>
      <v-client-table
        :columns="table_family.columns"
        :data="table_family.items"
        :options="table_family.options"
        class="table-default"
      >
        <template #h__relative>
          <div class="table-default__left">Relationship</div>
        </template>
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #relative="{ row }">
          <div class="table-default__left">{{ row.relationship.name }}</div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left">{{ row.full_name }}</div>
        </template>
        <template #phone="{ row }">{{ row.phone || '-' }}</template>
        <template #dob="{ row }">{{ dateFormat(row.dob) }}</template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button type="button" @click="onShowModalEdit(row, 'family')">
              <img alt="" src="@/assets/img/edit-table-icon.svg">
            </button>
            <button type="button" @click="onShowModalDelete(row, 'family')">
              <img alt="" src="@/assets/img/delete-table-icon.svg">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells>
        <h2 class="title">Emergency contacts</h2>
        <app-button size="small" @click.native="onOpenModalForm('contacts')">Add contact</app-button>
      </app-cells>
      <v-client-table
        :columns="table_emergency.columns"
        :data="table_emergency.items"
        :options="table_emergency.options"
        class="table-default"
      >
        <template #h__first_name>
          <div class="table-default__left">Name</div>
        </template>
        <template #first_name="{ row }">
          <div class="table-default__left">{{ row.first_name }} {{ row.last_name }}</div>
        </template>
        <template #relationship="{ row }">{{ row.relationship.name }}</template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button type="button" @click="onShowModalEdit(row, 'contacts')">
              <img alt="" src="@/assets/img/edit-table-icon.svg">
            </button>
            <button type="button" @click="onShowModalDelete(row, 'contacts')">
              <img alt="" src="@/assets/img/delete-table-icon.svg">
            </button>
          </div>
        </template>
      </v-client-table>
    </template>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="400"
      class="modal-not-overflow"
      draggable=".popup__title"
      name="family"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('family')"></button>
        <div class="popup__title">{{ current_modal_state === 'create' ? 'Add' : 'Edit' }} family member</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckModalForm">
            <app-form-group label="Relationship" required>
              <app-select
                v-model="family.relationship"
                :clearable="false"
                :error="$v.family.relationship.$error"
                :options="relative_options"
                placeholder="Relationship"
              />
              <template #error>
                <div v-if="$v.family.relationship.$dirty && !$v.family.relationship.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="First name" label-for="family_first_name" required>
              <app-input
                id="family_first_name"
                v-model="family.first_name"
                :error="$v.family.first_name.$error"
                name="family_first_name"
                placeholder="John"
                @change.native="$v.family.first_name.$touch()"
              />
              <template #error>
                <div v-if="$v.family.first_name.$dirty && !$v.family.first_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Middle name" label-for="family_middle_name">
              <app-input
                id="family_middle_name"
                v-model="family.middle_name"
                name="family_middle_name"
                placeholder="Emily"
              />
            </app-form-group>
            <app-form-group label="Last name" label-for="family_last_name" required>
              <app-input
                id="family_last_name"
                v-model="family.last_name"
                :error="$v.family.last_name.$error"
                name="family_last_name"
                placeholder="Doe"
                @change.native="$v.family.last_name.$touch()"
              />
              <template #error>
                <div v-if="$v.family.last_name.$dirty && !$v.family.last_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Phone">
              <app-phone
                v-model="family.phone"
                placeholder="+255 786 350 350"
              />
            </app-form-group>
            <app-form-group label="Date of birth" required>
              <app-date-picker
                v-model="family.dob"
                :error="$v.family.dob.$error"
                placeholder="Specify a date"
              />
              <template #error>
                <div v-if="$v.family.dob.$dirty && !$v.family.dob.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Gross income per month (TSh)" label-for="gross_income" required>
              <app-input
                id="gross_income"
                v-model.number="family.gross_income"
                :error="$v.family.gross_income.$error"
                name="gross_income"
                placeholder="0"
                type="number"
                value="0"
                @change.native="$v.family.gross_income.$touch()"
              />
              <template #error>
                <div v-if="$v.family.gross_income.$dirty && !$v.family.gross_income.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button ref="submit_family" :disabled="$v.family.$error" type="submit">
                {{ current_modal_state === 'create' ? 'Add family' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="contacts"
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="400"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('contacts')"></button>
        <div class="popup__title">{{ current_modal_state === 'create' ? 'Add' : 'Edit' }} emergency contacts</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckModalForm">
            <app-form-group label="First name" label-for="contacts_first_name" required>
              <app-input
                @change.native="$v.contacts.first_name.$touch()"
                v-model="contacts.first_name"
                :error="$v.contacts.first_name.$error"
                id="contacts_first_name"
                name="contacts_first_name"
                placeholder="John"
              />
              <template #error>
                <div v-if="$v.contacts.first_name.$dirty && !$v.contacts.first_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Last name" label-for="contacts_last_name" required>
              <app-input
                @change.native="$v.contacts.last_name.$touch()"
                v-model="contacts.last_name"
                :error="$v.contacts.last_name.$error"
                id="contacts_last_name"
                name="contacts_last_name"
                placeholder="Doe"
              />
              <template #error>
                <div v-if="$v.contacts.last_name.$dirty && !$v.contacts.last_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Phone" required>
              <app-phone
                v-model="contacts.phone"
                :error="$v.contacts.phone.$dirty && contacts.phone === 0"
                placeholder="+255 786 350 350"
                @change.native="$v.contacts.phone.$touch()"
              />
              <template #error>
                <div v-if="$v.contacts.phone.$dirty && contacts.phone === 0">
                  Invalid phone number format
                </div>
              </template>
            </app-form-group>
            <app-form-group label="Relationship" required>
              <app-select
                v-model="contacts.relationship"
                :options="relationship_options"
                :reduce="item => item.code"
                placeholder="Relationship"
                :error="$v.contacts.relationship.$error"
              />
              <template #error>
                <div v-if="$v.contacts.relationship.$dirty && !$v.contacts.relationship.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button ref="submit_contacts" :disabled="$v.contacts.$error" type="submit">
                {{ current_modal_state === 'create' ? 'Add contact' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-family"
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="400"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('delete-family')"></button>
        <div class="popup__title popup__title--flex">
          <img alt="" src="@/assets/img/warning-round-icon.svg">
          Delete Record
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this family member?</div>
          <div v-if="current_modal_data" class="popup__info">
            {{ current_modal_data.full_name }}
          </div>
          <app-cells :indent="false" position="center">
            <app-button theme="dark" @click.native="$modal.hide('delete-family')">Cancel</app-button>
            <app-button ref="delete_family" @click.native="onTableControlsDelete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="delete-contacts"
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="400"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('delete-contacts')"></button>
        <div class="popup__title popup__title--flex">
          <img alt="" src="@/assets/img/warning-round-icon.svg">
          Delete Record
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this emergency contact?</div>
          <div v-if="current_modal_data" class="popup__info">
            {{ current_modal_data.first_name }} {{ current_modal_data.last_name }}
          </div>
          <app-cells :indent="false" position="center">
            <app-button theme="dark" @click.native="$modal.hide('delete-contacts')">Cancel</app-button>
            <app-button ref="delete_contact" @click.native="onTableControlsDelete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteEmployeesEmergencyContact,
  deleteEmployeesFamily,
  getDictRelationships,
  getDisability,
  getEmployee,
  getEmployeesLanguageLevels,
  getEmployeesEmergencyContactList,
  getEmployeesFamily,
  getEmployeesSpokenLanguages,
  getGenders,
  getLanguages,
  getLocationRegions,
  getLocationVillages,
  getMaritalStatuses,
  getPaymentMethods,
  getRaces,
  patchEmloyee,
  patchEmployeesEmergencyContact,
  patchEmployeesFamily,
  patchEmployeesSpokenLanguages,
  postEmployeesEmergencyContact,
  postEmployeesFamily,
  postEmployeesSpokenLanguages,
  deleteEmployeesSpokenLanguages,
} from '@/http'
import { differenceInCalendarYears, format, parse, parseISO } from 'date-fns'
import { between, required } from 'vuelidate/lib/validators'
import PersonalCabinetAccess from '@/views/employees/employees/detail/PersonalCabinetAccess'

export default {
  name: 'EmployeeDetail',
  components: { PersonalCabinetAccess },
  data() {
    return {
      is_loaded: false,
      employee: {},
      show_personal: true,
      show_employment: true,
      show_documents: true,
      personal: {},
      age: 30,
      genders_options: [],
      company_options: [],
      payment_method_options: [],
      current_modal_type: '',
      family: {},
      contacts: {},
      relationship_options: [],
      current_modal_data: null,
      current_modal_state: 'create',
      table_family: {
        items: [],
        columns: ['name', 'phone', 'relative', 'dob', 'gross_income', 'controls'],
        options: {
          headings: {
            name: 'Name',
            phone: 'Phone',
            relative: 'Relative',
            dob: 'Date of birth',
            gross_income: 'Gross income',
            controls: ''
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries'
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      table_emergency: {
        items: [],
        columns: ['first_name', 'phone', 'relationship', 'controls'],
        options: {
          headings: {
            'first_name': 'Name',
            'phone': 'Phone',
            'relationship': 'Relationship',
            'controls': ''
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries'
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      race_options: [],
      marital_status_options: [],
      disability_options: [],
      relative_options: [],
      regions_options: [],
      villages_options: [],
      language_options: [],
      language_level_options: [],
      spoken_languages: [],
      strong_in_birds: false,
      great_with_kids: false,
      influencer_experienced: false,
      saving_spoken_language: null,
    }
  },
  validations: {
    personal: {
      first_name: { required },
      last_name: { required },
      dob: {},
      phone: {},
      additional_phone: {}
    },
    age: { between: between(16, 90) },
    family: {
      relationship: { required },
      first_name: { required },
      last_name: { required },
      dob: { required },
      gross_income: { required }
    },
    contacts: {
      first_name: { required },
      last_name: { required },
      phone: { required },
      relationship: { required }
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_email_readonly() {
      return Boolean(this.employee.user_account_active && !this.is_general_manager)
    },
    is_safari_tour_guide() {
      return this.employee.position && this.employee.position.code === 'safari_driver'
    }
  },
  created() {
    Promise.all([
      getEmployee(this.$route.params.id),
      getEmployeesEmergencyContactList(this.$route.params.id),
      getEmployeesFamily(this.$route.params.id)
    ])
      .then(values => {
        this.employee = values[0].data
        this.dataDistribution(values[0].data)

        if (this.is_safari_tour_guide) {
          this.strong_in_birds = Boolean(values[0].data.strong_in_birds)
          this.great_with_kids = Boolean(values[0].data.great_with_kids)
          this.influencer_experienced = Boolean(values[0].data.influencer_experienced)
          getEmployeesSpokenLanguages(this.$route.params.id).then(res => {
            this.spoken_languages = res.data.length ? res.data.map(this.normalizeSpokenLanguage) : [{ language: null, level: null }]
          })
        }

        this.table_emergency.items = values[1].data
        this.table_family.items = values[2].data

        this.is_loaded = true
      })
    getPaymentMethods().then(res => {
      this.payment_method_options = res.data
    })
    getDictRelationships().then(res => {
      this.relationship_options = res.data
    })
    getGenders().then(res => {
      this.genders_options = res.data
    })
    getRaces().then(res => {
      this.race_options = res.data
    })
    getMaritalStatuses().then(res => {
      this.marital_status_options = res.data
    })
    getDisability().then(res => {
      this.disability_options = res.data
    })
    getDictRelationships({ family: true }).then(res => {
      this.relative_options = res.data
    })
    getLocationRegions().then(res => {
      this.regions_options = res.data
    })
    getLocationVillages().then(res => {
      this.villages_options = res.data
    })
    getLanguages().then(res => {
      this.language_options = res.data
    })
    getEmployeesLanguageLevels().then(res => {
      this.language_level_options = res.data
    })
  },
  methods: {
    onPersonalCabinetStateChange(state) {
      this.$set(
        this.employee,
        'user_account_active',
        Boolean(state && state.account && state.account.is_active)
      )
    },
    normalizeSpokenLanguage(item) {
      return {
        id: item.id,
        language: item.language.code,
        level: item.level.code,
      }
    },
    onAddSpokenLanguage() {
      this.spoken_languages.push({ language: null, level: null })
    },
    onSaveSpokenLanguage(item, index) {
      this.saving_spoken_language = index
      const request = item.id ? patchEmployeesSpokenLanguages(this.$route.params.id, item.id, { level: item.level }) : postEmployeesSpokenLanguages(this.$route.params.id, { language: item.language, level: item.level })

      request
        .then(res => {
          this.$set(this.spoken_languages, index, this.normalizeSpokenLanguage(res.data))
          this.$notify({ type: 'success', title: 'Guide language saved' })
        })
        .catch(() => {
          this.$notify({ type: 'error', title: 'This language is already added or could not be saved' })
        })
        .finally(() => { this.saving_spoken_language = null })
    },
    onDeleteSpokenLanguage(item, index) {
      if (!item.id) {
        this.spoken_languages.splice(index, 1)
        return
      }
      deleteEmployeesSpokenLanguages(this.$route.params.id, item.id)
        .then(() => {
          this.spoken_languages.splice(index, 1)
          this.$notify({ type: 'success', title: 'Guide language deleted' })
        })
    },
    onSaveBirdwatchingExpert() {
      patchEmloyee(this.$route.params.id, { strong_in_birds: this.strong_in_birds })
        .then(() => {
          this.$notify({ type: 'success', title: 'Birdwatching expertise saved' })
        })
    },
    onSaveGuideSkill(field, title) {
      patchEmloyee(this.$route.params.id, { [field]: this[field] })
        .then(() => {
          this.$notify({ type: 'success', title: `${title} saved` })
        })
    },
    onCheckBirth() {
      this.$v.personal.dob.$touch()
      const date = parseISO(this.personal.dob)
      this.age = differenceInCalendarYears(new Date(), date)
    },
    onShowModalEdit(row, type) {
      this.current_modal_state = 'edit'
      this.current_modal_type = type
      this[type] = { ...row }
      this.$modal.show(type)
    },
    onShowModalDelete(row, type) {
      this.current_modal_data = row
      this.current_modal_type = type
      this.$modal.show(`delete-${type}`)
    },
    onTableControlsDelete() {
      if (this.current_modal_type === 'family') {
        this.$refs.delete_family.preload = true
        deleteEmployeesFamily(this.$route.params.id, this.current_modal_data.id)
          .finally(() => {
            this.$refs.delete_family.preload = false
          })
          .then(() => {
            getEmployeesFamily(this.$route.params.id).then(res => {
              this.table_family.items = res.data
            })
            this.$notify({
              type: 'success',
              title: `Family member ${this.current_modal_data.full_name} deleted`
            })
            this.$modal.hide('delete-family')
          })
      } else if (this.current_modal_type === 'contacts') {
        this.$refs.delete_contact.preload = true
        deleteEmployeesEmergencyContact(this.$route.params.id, this.current_modal_data.id)
          .finally(() => {
            this.$refs.delete_contact.preload = false
          })
          .then(() => {
            getEmployeesEmergencyContactList(this.$route.params.id).then(res => {
              this.table_emergency.items = res.data
            })
            this.$notify({
              type: 'success',
              title: `Emergency contact ${this.current_modal_data.first_name} ${this.current_modal_data.last_name} deleted`
            })
            this.$modal.hide('delete-contacts')
          })
      }
    },
    onCheckModalForm() {
      if (this.current_modal_type === 'family') {
        this.$v.family.$touch()
        if (this.$v.family.$invalid) {
          this.$notify({
            type: 'info',
            title: 'Check that the form fields are filled in correctly'
          })
        } else {
          this.sendModalForm()
        }
      } else if (this.current_modal_type === 'contacts') {
        this.$v.contacts.$touch()
        if (this.$v.contacts.$invalid) {
          this.$notify({
            type: 'info',
            title: 'Check that the form fields are filled in correctly'
          })
        } else {
          this.sendModalForm()
        }
      }
    },
    sendModalForm() {
      if (this.current_modal_type === 'family') {
        this.$refs.submit_family.preload = true
        if (this.current_modal_state === 'create') {
          postEmployeesFamily(this.$route.params.id, this.normalizeFamily())
            .finally(() => {
              this.$refs.submit_family.preload = false
            })
            .then(() => {
              getEmployeesFamily(this.$route.params.id).then(res => {
                this.table_family.items = res.data
              })
              this.$notify({
                type: 'success',
                title: 'Family member added'
              })
              this.$modal.hide('family')
            })
        } else {
          patchEmployeesFamily(this.$route.params.id, this.family.id, this.normalizeFamily())
            .finally(() => {
              this.$refs.submit_family.preload = false
            })
            .then(() => {
              getEmployeesFamily(this.$route.params.id).then(res => {
                this.table_family.items = res.data
              })
              this.$notify({
                type: 'success',
                title: `Family member ${this.family.first_name} ${this.family.last_name} saved`
              })
              this.$modal.hide('family')
            })
        }
      } else if (this.current_modal_type === 'contacts') {
        this.$refs.submit_contacts.preload = true
        if (this.current_modal_state === 'create') {
          postEmployeesEmergencyContact(this.$route.params.id, this.normalizeContacts())
            .finally(() => {
              this.$refs.submit_contacts.preload = false
            })
            .then(() => {
              getEmployeesEmergencyContactList(this.$route.params.id).then(res => {
                this.table_emergency.items = res.data
              })
              this.$notify({
                type: 'success',
                title: 'Emergency contact added'
              })
              this.$modal.hide('contacts')
            })
        } else {
          patchEmployeesEmergencyContact(this.$route.params.id, this.contacts.id, this.normalizeContacts())
            .finally(() => {
              this.$refs.submit_contacts.preload = false
            })
            .then(() => {
              getEmployeesEmergencyContactList(this.$route.params.id).then(res => {
                this.table_emergency.items = res.data
              })
              this.$notify({
                type: 'success',
                title: `Emergency contact ${this.contacts.first_name} ${this.contacts.last_name} saved`
              })
              this.$modal.hide('contacts')
            })
        }
      }
    },
    normalizeFamily() {
      const normalizedForm = { ...this.family }

      if (normalizedForm.relationship && typeof normalizedForm.relationship !== 'string') {
        normalizedForm.relationship = normalizedForm.relationship.code
      }
      return normalizedForm
    },
    normalizeContacts() {
      const normalizedForm = { ...this.contacts }

      if (normalizedForm.relationship && typeof normalizedForm.relationship !== 'string') {
        normalizedForm.relationship = normalizedForm.relationship.code
      }
      return normalizedForm
    },
    onOpenModalForm(type) {
      this.current_modal_state = 'create'
      this.current_modal_type = type
      this.contacts = {}
      this.warnings = {}
      this.family = {}
      this.family.gross_income = 0
      this.$v[type].$reset()
      this.$modal.show(type)
    },
    onCheckPersonalForm() {
      this.$v.personal.$touch()
      if (this.$v.personal.$invalid) {
        this.$notify({
          type: 'error',
          title: 'Check that the form fields are filled in correctly'
        })
      } else {
        if (this.personal.crop.coords && !this.personal.crop.photo) {
          this.$refs.submit_personal.preload = true
          this.$refs.cropper.sendCrop()
            .then(() => {
              this.sendPersonalForm()
            })
            .catch(() => {
              this.$refs.submit_personal.preload = false
            })
        } else {
          this.sendPersonalForm()
        }
      }
    },
    sendPersonalForm() {
      this.$refs.submit_personal.preload = true
      patchEmloyee(this.$route.params.id, this.normalizePersonalForm())
        .finally(() => {
          this.$refs.submit_personal.preload = false
        })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved successfully'
          })
          this.$refs.personalCabinetAccess.fetchState()
        })
    },
    normalizePersonalForm() {
      const normalizedForm = { ...this.personal }

      if (normalizedForm.marital_status && typeof normalizedForm.marital_status !== 'string') {
        normalizedForm.marital_status = normalizedForm.marital_status.code
      }
      if (normalizedForm.disability && typeof normalizedForm.disability !== 'string') {
        normalizedForm.disability = normalizedForm.disability.code
      }
      if (normalizedForm.race && typeof normalizedForm.race !== 'string') {
        normalizedForm.race = normalizedForm.race.code
      }
      if (normalizedForm.region && typeof normalizedForm.region !== 'number') {
        normalizedForm.region = normalizedForm.region.id
      }
      if (normalizedForm.village && typeof normalizedForm.village !== 'number') {
        normalizedForm.village = normalizedForm.village.id
      }

      return normalizedForm
    },
    dataDistribution(data) {
      this.employee = data
      this.personal = {
        first_name: data.first_name,
        middle_name: data.middle_name,
        last_name: data.last_name,
        resident: data.resident,
        village: data.village,
        region: data.region,
        dob: data.dob,
        gender: data.gender,
        crop: data.crop,
        email: data.email,
        phone: data.phone,
        phone_telegram: data.phone_telegram,
        phone_whatsapp: data.phone_whatsapp,
        marital_status: data.marital_status,
        disability: data.disability,
        race: data.race,
        additional_phone: data.additional_phone,
        additional_phone_telegram_available: data.additional_phone_telegram_available,
        additional_phone_whatsapp_available: data.additional_phone_whatsapp_available,
        telegram_id: data.telegram_id,
        telegram_username: data.telegram_username
      }
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    }
  }
}
</script>
<style lang="sass" scoped>
.safari-guide-expert
  display: flex
  align-items: flex-start
  gap: 8px
  margin: 0
  cursor: pointer

  input
    +visually-hidden

  & + .safari-guide-expert
    margin-top: 10px

.safari-guide-section
  margin-top: 32px
  margin-bottom: 28px

.safari-guide-expert__check
  display: flex
  align-items: center
  justify-content: center
  width: 16px
  height: 16px
  flex: 0 0 16px
  margin-top: 2px
  border: 1px solid #d8e1e7
  border-radius: 4px
  background: #f7fafb
  color: transparent
  font-size: 12px
  font-weight: 600

.safari-guide-expert--active
  .safari-guide-expert__check
    border-color: #77b017
    background: #77b017
    color: #fff

.safari-guide-expert__content
  display: grid
  gap: 2px

  strong
    color: #18212a
    font-size: 16px
    line-height: 1.25

  span
    color: #8a969f
    font-size: 13px
    line-height: 1.35

.spoken-languages
  margin-top: 22px

.spoken-languages__title
  margin: 0 0 12px
  color: #303b45
  font-size: 16px
  font-weight: 600

.spoken-languages__row
  display: flex
  align-items: center
  gap: 10px
  margin-bottom: 10px

.spoken-languages__language
  width: 360px

.spoken-languages__level
  width: 200px

.spoken-languages__add
  display: inline-flex
  align-items: center
  gap: 8px
  margin-top: 4px
  border: 0
  background: transparent
  color: #77b017
  font-size: 14px
  font-weight: 600
  cursor: pointer

  span
    display: flex
    align-items: center
    justify-content: center
    width: 22px
    height: 22px
    border: 2px solid #77b017
    border-radius: 50%
    font-size: 18px
    line-height: 1

.spoken-languages__hint
  margin-top: 12px
  color: #8a969f
  font-size: 13px

@media (max-width: 800px)
  .spoken-languages__row
    align-items: stretch
    flex-direction: column

  .spoken-languages__language,
  .spoken-languages__level
    width: 100%

.slide-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-enter-to, .slide-leave
  max-height: 768px
  overflow: hidden

.slide-enter, .slide-leave-to
  overflow: hidden
  max-height: 0

.slide-small-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-small-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-small-enter-to, .slide-small-leave
  max-height: 147px
  overflow: hidden

.slide-small-enter, .slide-small-leave-to
  overflow: hidden
  max-height: 0
</style>
